import React from 'react';
import { UserStatus } from '../../context/PresenceContext';

const statusColors = {
  [UserStatus.AVAILABLE]: 'bg-green-500',
  [UserStatus.BUSY]: 'bg-red-500',
  [UserStatus.AWAY]: 'bg-yellow-500',
  [UserStatus.DO_NOT_DISTURB]: 'bg-red-600',
  [UserStatus.OFFLINE]: 'bg-gray-400',
  [UserStatus.IDLE]: 'bg-amber-400',
  [UserStatus.IN_MEETING]: 'bg-cyan-500'
};

const statusLabels = {
  [UserStatus.AVAILABLE]: 'Available',
  [UserStatus.BUSY]: 'Busy',
  [UserStatus.AWAY]: 'Away',
  [UserStatus.DO_NOT_DISTURB]: 'Do Not Disturb',
  [UserStatus.OFFLINE]: 'Offline',
  [UserStatus.IDLE]: 'Idle',
  [UserStatus.IN_MEETING]: 'In Meeting'
};

/**
 * Display status colors for activity tracking (green/yellow/red)
 */
const displayStatusColors = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500'
};

const displayStatusLabels = {
  green: 'Active',
  yellow: 'Idle',
  red: 'Offline'
};

/**
 * Small status dot indicator
 */
export const StatusDot = ({ status, size = 'sm', className = '' }) => {
  const sizeClasses = {
    xs: 'w-2 h-2',
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  return (
    <span
      className={`${sizeClasses[size]} ${statusColors[status] || statusColors.offline} rounded-full ${className}`}
      title={statusLabels[status]}
    />
  );
};

/**
 * Display status dot for activity tracking (green/yellow/red)
 * Used in team presence list to show activity state
 */
export const DisplayStatusDot = ({ displayStatus, size = 'sm', className = '' }) => {
  const sizeClasses = {
    xs: 'w-2 h-2',
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  return (
    <span
      className={`${sizeClasses[size]} ${displayStatusColors[displayStatus] || displayStatusColors.red} rounded-full ${className}`}
      title={displayStatusLabels[displayStatus]}
    />
  );
};

/**
 * Status badge with label
 */
export const StatusBadge = ({ status, showLabel = true, size = 'sm' }) => {
  return (
    <div className="flex items-center gap-1.5">
      <StatusDot status={status} size={size} />
      {showLabel && (
        <span className="text-xs text-slate-400">
          {statusLabels[status]}
        </span>
      )}
    </div>
  );
};

/**
 * Status selector dropdown
 */
export const StatusSelector = ({ currentStatus, onSelect, className = '' }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const statuses = [
    UserStatus.AVAILABLE,
    UserStatus.BUSY,
    UserStatus.AWAY,
    UserStatus.DO_NOT_DISTURB
  ];

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition"
      >
        <StatusDot status={currentStatus} size="sm" />
        <span className="text-sm text-slate-300">{statusLabels[currentStatus]}</span>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-48 bg-slate-800 border border-cyan-500/20 rounded-lg shadow-xl z-50 py-1">
          {statuses.map(status => (
            <button
              key={status}
              onClick={() => {
                onSelect(status);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-cyan-500/20 transition ${
                currentStatus === status ? 'text-cyan-300 bg-cyan-500/10' : 'text-slate-300'
              }`}
            >
              <StatusDot status={status} size="sm" />
              {statusLabels[status]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusDot;
